/**
 * Attendance System Configuration
 * 
 * This file contains configurable settings for the attendance system.
 * These settings can be modified here to adjust system behavior without
 * changing the core logic.
 * 
 * FUTURE ENHANCEMENT: These settings should be moved to Firestore
 * to allow dynamic configuration by admins without code deployment.
 */

import { AttendanceSettings } from '@/types/attendance';

/**
 * Default Attendance Configuration
 * 
 * Modify these values to adjust attendance system behavior:
 * 
 * - officeRadius: Maximum distance (in meters) allowed for check-in
 * - shiftStartTime: Time after which attendance is marked as late (HH:mm format)
 * - gracePeriodMinutes: Minutes allowed after shift start before marking late
 * - lateThresholdMinutes: Additional minutes after grace period for late threshold
 * - shiftEndTime: Expected end time of shift (HH:mm format)
 */
export const ATTENDANCE_CONFIG: AttendanceSettings = {
  officeRadius: 200, // 200 meters
  shiftStartTime: "08:00", // 8:00 AM
  shiftEndTime: "18:00", // 6:00 PM
  gracePeriodMinutes: 0, // No grace period
  lateThresholdMinutes: 0, // Immediately late after shift start
};

/**
 * Get current attendance configuration
 * 
 * @returns Current attendance settings
 * 
 * @future This function will fetch settings from Firestore in future updates
 */
export function getAttendanceConfig(): AttendanceSettings {
  return ATTENDANCE_CONFIG;
}

/**
 * Format shift time for display
 * 
 * @param timeString - Time in HH:mm format (e.g., "10:35")
 * @returns Formatted time string (e.g., "10:35 AM")
 */
export function formatShiftTime(timeString: string): string {
  const [hours, minutes] = timeString.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Configuration Notes:
 * 
 * 1. OFFICE RADIUS (200m):
 *    - Increase if office location is inaccurate
 *    - Decrease for stricter enforcement
 * 
 * 2. SHIFT START TIME (10:35 AM):
 *    - Change to match your office timing
 *    - Must be in 24-hour format (HH:mm)
 * 
 * 3. GRACE PERIOD (0 minutes):
 *    - Set to > 0 to allow buffer time
 *    - Example: 5 means late after 10:40 AM
 * 
 * 4. LATE THRESHOLD (0 minutes):
 *    - Currently unused (grace period = 0)
 *    - Can be used for multi-tier late system
 */
